import React, { useState, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import stravaButtonImg from './btn_strava_connect_with_orange_x2.svg';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://f155c6e67830.ngrok-free.app/v1';

interface ProviderStatus {
  provider: string;
  connected: boolean;
  lastSync?: Date;
}

const SuccessPage = () => {
  const [searchParams] = useSearchParams();
  const connectedProvider = searchParams.get('provider');
  const providersParam = searchParams.get('providers'); // Para fluxo chained do Garmin
  const userId = searchParams.get('userId');
  const [error, setError] = useState<string | null>(null);
  const [connectedProviders, setConnectedProviders] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);

  const getProviderDisplayName = (provider: string) => {
    if (provider === 'strava') return 'Strava';
    if (provider === 'oura') return 'Oura';
    if (provider === 'garmin') return 'Garmin';
    if (provider === 'garmin-health') return 'Garmin Health';
    return provider;
  };

  // Lista de providers disponíveis (Oura comentado)
  const availableProviders = [
    { id: 'strava', name: 'Strava', image: stravaButtonImg },
    { id: 'garmin', name: 'Garmin' },
    // { id: 'oura', name: 'Oura' }, // Comentado conforme solicitado
  ];

  // Buscar status das conexões do usuário
  useEffect(() => {
    const fetchProviderStatus = async () => {
      if (!userId) {
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get<{ providers: ProviderStatus[] }>(
          `${API_BASE_URL}/auth/connections/status?userId=${userId}`
        );
        
        console.log('[SuccessPage] Provider status response:', response.data);
        
        // Criar um Set com os providers que estão conectados
        const connected = new Set(
          response.data.providers
            .filter(p => p.connected)
            .map(p => p.provider)
        );
        
        // Adicionar o provider que acabou de ser conectado (se houver)
        if (connectedProvider) {
          connected.add(connectedProvider);
        }
        
        // Se houver parâmetro providers (fluxo chained do Garmin), adicionar todos
        if (providersParam) {
          const providersList = providersParam.split(',');
          providersList.forEach(p => connected.add(p.trim()));
        }
        
        // Lógica especial para Garmin: se garmin OU garmin-health estiver conectado, considerar ambos
        if (connected.has('garmin') || connected.has('garmin-health')) {
          connected.add('garmin');
          connected.add('garmin-health');
        }
        
        console.log('[SuccessPage] Connected providers:', Array.from(connected));
        
        setConnectedProviders(connected);
      } catch (err: any) {
        console.error('Error fetching provider status:', err);
        // Em caso de erro, ainda mostrar os botões baseado apenas no provider atual
        if (connectedProvider) {
          const connected = new Set([connectedProvider]);
          
          // Se houver parâmetro providers (fluxo chained), adicionar todos
          if (providersParam) {
            const providersList = providersParam.split(',');
            providersList.forEach(p => connected.add(p.trim()));
          }
          
          // Se conectou garmin ou garmin-health, considerar ambos
          if (connectedProvider === 'garmin' || connectedProvider === 'garmin-health') {
            connected.add('garmin');
            connected.add('garmin-health');
          }
          
          setConnectedProviders(connected);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchProviderStatus();
  }, [userId, connectedProvider, providersParam]);

  // Filtrar providers que ainda não foram conectados
  const otherProviders = availableProviders.filter(
    (provider) => 
      provider.id !== 'garmin-health' && 
      !connectedProviders.has(provider.id)
  );

  const handleConnect = useCallback(async (provider: string) => {
    if (!userId) {
      setError('Não é possível conectar sem um ID de usuário.');
      return;
    }

    try {
      const params = new URLSearchParams({
        userId,
        ...(provider === 'garmin' && { chainHealthAuth: 'true' }),
      });
      const url = provider === 'garmin' 
        ? `${API_BASE_URL}/auth/${provider}?${params}` 
        : `${API_BASE_URL}/auth/${provider}?userId=${userId}`;
      
      const response = await axios.get(url);
      const { authUrl } = response.data;

      if (authUrl) {
        window.location.href = authUrl;
      } else {
        setError(`Não foi possível obter a URL de autorização para ${provider}.`);
      }
    } catch (err: any) {
      console.error(`Error initiating ${provider} connection:`, err);
      setError(err.response?.data?.message || 'Ops! Houve um problema na comunicação com o provedor. Por favor, tente novamente em alguns instantes.');
    }
  }, [userId]);

  return (
    <div className="min-h-screen bg-transparent text-white flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-lg mx-auto text-center bg-black/50 backdrop-blur-lg p-8 rounded-xl border border-white/20">
        
        {connectedProvider ? (
          <>
            <h1 className="text-4xl font-bold mb-4 text-green-400">Login realizado com sucesso!</h1>
            <p className="text-lg mb-8">
              Sua conta {getProviderDisplayName(connectedProvider)} foi conectada com sucesso. Já estamos sincronizando seu histórico de dados.
            </p>
            <p className="text-base text-gray-300 mb-6">
              Você receberá uma confirmação via WhatsApp em instantes.
            </p>
          </>
        ) : (
          <>
            <h1 className="text-4xl font-bold mb-4">Login realizado com sucesso!</h1>
            <p className="text-lg mb-8">
              Sua conta foi conectada com sucesso! Já estamos sincronizando seu histórico de dados.
            </p>
            <p className="text-base text-gray-300 mb-6">
              Você receberá uma confirmação via WhatsApp em instantes.
            </p>
          </>
        )}

        {/* Mostrar opções de outros providers se houver provider conectado e outros disponíveis */}
        {!loading && connectedProvider && otherProviders.length > 0 && userId && (
          <div className="mt-8 border-t border-white/20 pt-6">
            <p className="text-lg mb-4">
              Quer conectar mais aplicativos? Conecte também:
            </p>
            <div className="flex flex-col items-center space-y-4">
              {otherProviders.map((provider) => (
                provider.id === 'strava' ? (
                  <button
                    key={provider.id}
                    className="bg-transparent border-none p-0 cursor-pointer transition duration-300 px-6 ease-in-out transform hover:scale-105"
                    onClick={() => handleConnect(provider.id)}
                    aria-label={`Conectar com ${provider.name}`}
                  >
                    <img 
                      src={stravaButtonImg} 
                      alt={`Botão para conectar com ${provider.name}`} 
                      className="w-[220px]"
                    />
                  </button>
                ) : (
                  <button
                    key={provider.id}
                    className="bg-blue-500 hover:bg-blue-600 text-white font-bold py-3 px-6 rounded-lg text-md transition duration-300 ease-in-out transform hover:scale-105"
                    onClick={() => handleConnect(provider.id)}
                  >
                    Conectar com {provider.name}
                  </button>
                )
              ))}
            </div>
            {error && (
              <p className="text-red-400 text-sm mt-4">{error}</p>
            )}
          </div>
        )}

        <div className="mt-8 border-t border-white/20 pt-6">
          <p className="text-lg">
            Você pode fechar esta página e voltar ao WhatsApp. Uma mensagem de confirmação será enviada em breve.
          </p>
        </div>

      </div>
    </div>
  );
};

export default SuccessPage;